// ── 2 Lizenz ── Zustand und Rumpf (rein), dazu die Benutzer-Lizenzen
// (docs/11 8e): je Lizenz-Vorlage eine Zahl Plätze. Die Mandantenlizenz darüber
// bleibt die Obergrenze für alles; eine Benutzer-Lizenz schneidet darunter je
// Konto. „Aus" = der Mandant läuft ohne.

import type { Catalog, LicenseTemplate, SeatMode, Tenant } from "../../api/types";
import { eur, numOrNull } from "../../lib/format";
import { pickOption, type SelectOptions } from "../../ui/select-menu";

export const SEAT_MODE: SelectOptions = [
  ["off", "Aus — der Mandant läuft ohne Benutzer-Lizenzen"],
  ["manual", "Manuell — der Tenant-Admin weist zu, ohne Lizenz kein Zugang"],
  ["auto", "Automatisch — neue Benutzer bekommen die Standard-Lizenz, solange Plätze frei sind"],
];

export function defaultSeatOptions(templates: readonly LicenseTemplate[]): SelectOptions {
  return [["", "— wählen —"], ...templates.map((tpl): readonly [string, string] => [String(tpl.id), tpl.name])];
}

export interface LicenseState {
  max_users: string;
  max_workspaces: string;
  memory_mb: string;
  cpus: string;
  valid_until: string;
  /** Angehakte Features (auch bei „alle", wie die Checkboxen früher). */
  features: string[];
  all_features: boolean;
  watchers: boolean;
  voice: boolean;
  custom_mcp: boolean;
  seat_mode: string;
  /** Vorlage (id) → eingetippte Zahl, als Text wie im Feld. */
  seats: Record<string, string>;
  default_seat: string;
}

export interface LicenseBody {
  license: {
    max_users: number | null;
    max_workspaces: number | null;
    features: string[] | null;
    watchers: boolean;
    voice: boolean;
    custom_mcp: boolean;
    workspace_limits: { memory_mb: number | null; cpus: number | null };
    valid_until: string | null;
    seat_mode: SeatMode;
    seats: Array<{ template_id: number; count: number }>;
    default_seat: number | null;
  };
}

const text = (v: number | string | null | undefined): string => String(v ?? "");

export function licenseFromTenant(t: Tenant, cat: Catalog): LicenseState {
  const l = t.license;
  const all = l.features === null;
  const templates = cat.license_templates ?? [];
  return {
    max_users: text(l.max_users),
    max_workspaces: text(l.max_workspaces),
    memory_mb: text(l.workspace_limits.memory_mb),
    cpus: text(l.workspace_limits.cpus),
    valid_until: l.valid_until ?? "",
    features: cat.features.filter((f) => all || (l.features ?? []).includes(f)),
    all_features: all,
    watchers: l.watchers,
    voice: l.voice,
    custom_mcp: l.custom_mcp,
    seat_mode: pickOption(l.seat_mode, SEAT_MODE),
    seats: Object.fromEntries(templates.map((tpl) => [String(tpl.id), text(l.seats.find((x) => x.template_id === tpl.id)?.count)])),
    // Eine Standard-Lizenz, deren Vorlage es nicht mehr gibt, wird zu „— wählen —".
    default_seat: pickOption(l.default_seat, defaultSeatOptions(templates)),
  };
}

/**
 * Die Plätze: je Vorlage des Katalogs die eingetippte Zahl — auch wenn der
 * Block bei „Aus" versteckt ist (die Felder standen früher trotzdem im
 * Formular). Leer, 0 und Unsinn (`NaN`) entfallen.
 */
export function collectSeats(s: LicenseState, templates: readonly LicenseTemplate[]): Pick<LicenseBody["license"], "seat_mode" | "seats" | "default_seat"> {
  const seats = templates
    .map((tpl) => ({ template_id: tpl.id, count: Number(s.seats[String(tpl.id)] || 0) }))
    .filter((x) => x.count > 0);
  return {
    seat_mode: s.seat_mode as SeatMode,
    seats,
    default_seat: s.seat_mode === "auto" && s.default_seat ? Number(s.default_seat) : null,
  };
}

export function licenseToBody(s: LicenseState, cat: Catalog): LicenseBody {
  return {
    license: {
      max_users: numOrNull(s.max_users),
      max_workspaces: numOrNull(s.max_workspaces),
      // Reihenfolge des Katalogs — so standen die Checkboxen im Formular.
      features: s.all_features ? null : cat.features.filter((f) => s.features.includes(f)),
      watchers: s.watchers,
      voice: s.voice,
      custom_mcp: s.custom_mcp,
      workspace_limits: { memory_mb: numOrNull(s.memory_mb), cpus: numOrNull(s.cpus) },
      valid_until: s.valid_until || null,
      ...collectSeats(s, cat.license_templates ?? []),
    },
  };
}

/** Was die Plätze kosten: je Vorlage „x € / Monat" und die Summe darunter. */
export function seatSums(seats: Record<string, string>, templates: readonly LicenseTemplate[]): { per: Record<string, string>; total: string } {
  let total = 0;
  let count = 0;
  const per: Record<string, string> = {};
  for (const tpl of templates) {
    const n = Number(seats[String(tpl.id)] || 0);
    const price = tpl.price_eur_month;
    count += n;
    per[String(tpl.id)] = n > 0 && price !== null ? `${eur(n * price)} / Monat` : "";
    if (price !== null) total += n * price;
  }
  return { per, total: count ? `${count} Plätze · ${eur(total)} je Monat` : "" };
}
